import { useEffect, useRef, useState } from 'react'
import {
  RemoteAudioTrack,
  RemoteVideoTrack,
  Participant as TwilioParticipant,
} from 'twilio-video'

import { useCallManager } from '../../../contexts/call_manager_context'
import usePreviousProps from '../../../hooks/previous_props_hook'
import { LocalTrackData } from '../tracks/use_local_tracks'

export const useRemoteParticipantsWithVideo = (
  remoteParticipants: TwilioParticipant[] | undefined,
  localTrackData: LocalTrackData,
): TwilioParticipant[] | undefined => {
  const previousRemoteParticipants = usePreviousProps(remoteParticipants)
  const [participantsWithVideo, setParticipantsWithVideo] =
    useState<TwilioParticipant[]>()
  const subscribedListenerRef = useRef<{
    [index: string]: (track: RemoteAudioTrack | RemoteVideoTrack) => void
  }>({})
  const unsubscribedListenerRef = useRef<{
    [index: string]: (track: RemoteAudioTrack | RemoteVideoTrack) => void
  }>({})
  const {
    activeCall: { navToChatView },
  } = useCallManager()
  const isLocalParticipantVideo =
    !!localTrackData &&
    (localTrackData.isVideoEnabled || localTrackData.isScreenShareEnabled)

  useEffect(() => {
    if (!remoteParticipants) {
      setParticipantsWithVideo(undefined)
      return
    }
    if (remoteParticipants.length === 0) {
      setParticipantsWithVideo([])
      return
    }
    const newState: TwilioParticipant[] = []

    const subscribedListeners = subscribedListenerRef.current
    const unsubscribedListeners = unsubscribedListenerRef.current

    remoteParticipants.forEach((participant) => {
      subscribedListeners[participant.sid] = (
        track: RemoteAudioTrack | RemoteVideoTrack,
      ) => {
        if (track.kind === 'video') {
          setParticipantsWithVideo((state) => {
            if (!state) {
              return state
            }
            if (state.indexOf(participant) === -1) {
              return [...state, participant]
            }

            return [...state]
          })
        }
      }

      unsubscribedListeners[participant.sid] = (
        track: RemoteAudioTrack | RemoteVideoTrack,
      ) => {
        if (track.kind === 'video') {
          setParticipantsWithVideo((state) => {
            if (!state) {
              return state
            }
            const index = state.indexOf(participant)
            if (index !== -1) {
              if (state.length === 1 && !isLocalParticipantVideo) {
                /* this is actually called elsewhere, however it is called here as well to prevent the video preview
                 dialog briefly rendering before it navigates to the chat view, and asking for video permissions if they
                 haven't allowed yet.  */
                navToChatView && navToChatView()
              }
              state.splice(index, 1)
            }
            return [...state]
          })
        }
      }

      participant.on('trackSubscribed', subscribedListeners[participant.sid])
      participant.on(
        'trackUnsubscribed',
        unsubscribedListeners[participant.sid],
      )

      if (participant.videoTracks.size > 0) {
        newState.push(participant)
      }
    })

    if (remoteParticipants !== previousRemoteParticipants) {
      setParticipantsWithVideo(newState)
    }

    return () => {
      remoteParticipants.forEach((participant) => {
        participant.off('trackSubscribed', subscribedListeners[participant.sid])
        participant.off(
          'trackUnsubscribed',
          unsubscribedListeners[participant.sid],
        )
      })
      subscribedListenerRef.current = {}
      unsubscribedListenerRef.current = {}
    }
  }, [
    isLocalParticipantVideo,
    navToChatView,
    previousRemoteParticipants,
    remoteParticipants,
  ])

  return participantsWithVideo
}
